import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import { clearAuth, readAuth, writeAuth, type AuthData } from "@/lib/auth"

type AuthContextValue = {
  auth: AuthData | null
  isAuthenticated: boolean
  signIn: (data: AuthData) => void
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * AuthProvider — single source of truth for the current admin's auth.
 *
 * - Initializes from localStorage so a logged-in legacy session carries over.
 * - Listens to the `storage` event so signing out in one tab signs out all
 *   tabs (also catches changes the legacy admin makes to auth_data, since
 *   both apps share the same key).
 * - Doesn't poll; doesn't hold the token in memory longer than needed.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<AuthData | null>(() => readAuth())

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== "auth_data") return
      setAuth(readAuth())
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  const signIn = useCallback((data: AuthData) => {
    writeAuth(data)
    setAuth(data)
  }, [])

  const signOut = useCallback(() => {
    clearAuth()
    setAuth(null)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      auth,
      isAuthenticated: auth !== null,
      signIn,
      signOut,
    }),
    [auth, signIn, signOut]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>")
  }
  return ctx
}
